import {ColumnFilterItem} from 'antd/es/table/interface';
import {OptionType} from 'components/form/Select';
import {showError} from 'helpers/alerts';
import {t} from 'i18next';
import {action, computed, observable, runInAction} from 'mobx';
import {browserHistory} from 'routing/browserHistory';
import {AuthRoutePaths} from 'routing/Routes';
import {
  CarBodyType,
  CarListDto,
  CarPaintProtectionType,
  CarStatus,
  CommonDataDto,
  CommonDataDtoGenericApiResponse,
  CommonDataGarageDto,
  DriveType,
  EmployeeDto,
  FuelType,
  GarageDto,
  GarageSpotAttributeDefinitionDto,
  GearType,
  PaintEffectType,
  UserDto,
} from '../../../../PRMGApiClient';
import {Api} from '../../../../PRMGApiClient/ApiClient';

export abstract class AppCommonData {
  @observable
  commonData: CommonDataDto | undefined;

  isLoading: boolean = false;

  constructor() {
    this.getCommonData();
  }

  abstract initStates(): void;

  @computed
  get user(): UserDto | undefined {
    return this.commonData?.user;
  }

  @computed
  get garages(): GarageDto[] {
    return this.commonData?.garages ?? [];
  }

  @computed
  get attributeDefinitions(): GarageSpotAttributeDefinitionDto[] {
    return this.commonData?.atributeDefinitions ?? [];
  }

  @computed
  get allCars(): Array<CarListDto | undefined> {
    return (
      this.commonData?.garages
        ?.map((garage) => garage.garageSpots)
        .flat(1)
        .map((spot) => spot?.car) ?? []
    );
  }

  @computed
  get employees(): Array<EmployeeDto> {
    return this.commonData?.employees ?? [];
  }

  @computed
  get employeesOptions(): Array<OptionType> {
    return this.employees.map((employee) => ({
      value: employee.id,
      label: `${employee.firstName} ${employee.lastName}`,
    })) as OptionType[];
  }

  @computed
  get markOptions(): OptionType[] {
    return this.commonData?.carMakesWithModels?.map((mark) => ({
      value: mark.id,
      label: mark.makeName,
    })) as OptionType[];
  }

  @computed
  get addedCarsMarkOptions() {
    const addedCarsMarksIds = this.allCars.map((car) => car?.carMakeId);
    return this.markOptions.filter((markOption) => addedCarsMarksIds.includes(markOption.value as number));
  }

  @computed
  get statusOptions(): OptionType[] {
    return Object.values(CarStatus).map((status) => ({
      value: status,
      label: status,
    }));
  }

  @computed
  get carBrandModelsTree(): ColumnFilterItem[] {
    return (
      this.commonData?.carMakesWithModels?.map((carMake) => ({
        text: carMake.makeName ?? '',
        value: `brand-${carMake.id}` ?? '',
        children: (carMake.models ?? []).map((carModel) => ({
          text: carModel.modelName ?? '',
          value: `model-${carModel.carModelId}` ?? '',
        })),
      })) ?? []
    );
  }

  @computed
  get manufactureYearOptions(): OptionType[] {
    return Array.from({length: 100}, (_, i) => ({
      value: new Date().getFullYear() - i,
      label: new Date().getFullYear() - i,
    }));
  }

  @computed
  get colorOptions(): OptionType[] {
    return this.commonData?.colorDictionary?.map((color) => ({
      value: color.colorDictionaryId,
      label: color.colorName,
    })) as OptionType[];
  }

  @computed
  get bodyTypeOptions(): OptionType[] {
    return Object.values(CarBodyType).map((item) => ({
      value: item,
      label: item,
    }));
  }

  @computed
  get fuelOptions(): OptionType[] {
    return Object.values(FuelType).map((item) => ({
      value: item,
      label: item,
    }));
  }

  @computed
  get driveOptions(): OptionType[] {
    return Object.values(DriveType).map((item) => ({
      value: item,
      label: item,
    }));
  }

  @computed
  get gearOptions(): OptionType[] {
    return Object.values(GearType).map((item) => ({
      value: item,
      label: item,
    }));
  }

  @computed
  get paintEffectOptions(): OptionType[] {
    return Object.values(PaintEffectType).map((item) => ({
      value: item,
      label: item,
    }));
  }

  @computed
  get paintProtectionOptions(): OptionType[] {
    return Object.values(CarPaintProtectionType).map((item) => ({
      value: item,
      label: item,
    }));
  }

  async refreshFileLink(fileId: number): Promise<string | null | undefined> {
    const response = await Api.FileApi.getFileById({id: fileId});
    return response.content?.url;
  }

  getGarage(garageId: number): CommonDataGarageDto | undefined {
    return this.commonData?.garages?.find((garage: CommonDataGarageDto) => garage.id === garageId);
  }

  @action
  async getCommonData(): Promise<void> {
    this.isLoading = true;
    const isAccountConfirmedFromEmailLink = location.search.includes('accountAlreadyConfirmedCallback=true');
    return Api.CommonData.apiCommonDataGet()
      .then((res: CommonDataDtoGenericApiResponse) => {
        runInAction(() => {
          this.commonData = res.content;
          this.initStates();
        });
        if (isAccountConfirmedFromEmailLink) {
          showError(t('Account already registered'));
        }
      })
      .catch((e) => {
        if (e.status !== 400 && e.status !== 401) {
          console.error(e);
        }
        if (isAccountConfirmedFromEmailLink) {
          showError(t('Your account has already been created, please log in'));
          browserHistory.replace(AuthRoutePaths.signIn.url);
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
